import React, { useMemo, useState } from "react";
import moment from "moment";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { Button, Iconography, IconographySizes, Pagination } from "@hid-galaxy-ui/galaxy-react";

type TableData = {
  [key: string]: any;
};

export type SortRule = {
  column: string;
  direction: "asc" | "desc";
};

type TableColumn = {
  value: string;
  label: string;
  dataKey?: string;
};

type DataTableProps = {
  rows: TableData[];
  columns: TableColumn[];
  columnSorts: SortRule[];
  setColumnSorts: React.Dispatch<React.SetStateAction<SortRule[]>>;
};

const getValue = (row: TableData, path: string) => {
  if (!path) return undefined;
  const parts = path.split(".");
  let cur: any = row;
  let traversed = true;
  for (const p of parts) {
    if (cur != null && Object.prototype.hasOwnProperty.call(cur, p)) {
      cur = cur[p];
    } else {
      traversed = false;
      break;
    }
  }
  if (traversed) return cur;

  const last = parts[parts.length - 1];
  if (Object.prototype.hasOwnProperty.call(row, last)) return row[last];
  return row[path];
};

const getColumnDataKey = (col: TableColumn) =>
  (col.dataKey && col.dataKey.trim()) || col.value.split(".").pop() || col.value;

// Convert date-like value
const toDate = (v: any): Date | null => {
  if (v instanceof Date && !isNaN(v.getTime())) return v;
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

//Comparison: numbers, dates, then strings (case-insensitive, numeric aware)
const compareValues = (a: any, b: any) => {
  const aNull = a == null || a === "";
  const bNull = b == null || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }

  const aDate = toDate(a);
  const bDate = toDate(b);
  if (aDate && bDate) {
    return aDate.getTime() - bDate.getTime();
  }

  const aStr = String(a);
  const bStr = String(b);
  return aStr.localeCompare(bStr, undefined, { sensitivity: "base", numeric: true });
};

const DataTable: React.FC<DataTableProps> = ({
  rows,
  columns,
  columnSorts,
  setColumnSorts,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentCol, setCurrentCol] = useState<string>("");

  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(15);


  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      return !columns.every((col) => {
        const v = getValue(row, getColumnDataKey(col));
        return v == null || (typeof v === "string" && v.trim() === "");
      });
    });
  }, [rows, columns]);


  const sortedRows = useMemo(() => {
    if (!columnSorts || columnSorts.length === 0) return filteredRows;

    const withIndex = filteredRows.map((r, idx) => ({ r, idx }));

    withIndex.sort((a, b) => {
      for (const rule of columnSorts) {
        const av = getValue(a.r, rule.column);
        const bv = getValue(b.r, rule.column);
        const cmp = compareValues(av, bv);
        if (cmp !== 0) {
          return rule.direction === "asc" ? cmp : -cmp;
        }
      }
      return a.idx - b.idx;
    });

    return withIndex.map((x) => x.r);
  }, [filteredRows, columnSorts]);


  const totalRecords = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.max(1, Math.min(currentPageNumber, totalPages));

  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;

  const pagedRows = useMemo(() => {
    return sortedRows.slice(start, end);
  }, [sortedRows, start, end]);

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber ?? 1);
  };

  const onPageSizesClick = (_e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };


  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    col: string
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentCol(col);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentCol("");
  };


  const handleSortChange = (direction: "asc" | "desc" | "") => {
    setColumnSorts((prev) => {
      const withoutCurrent = prev.filter((s) => s.column !== currentCol);

      if (!direction) {
        return withoutCurrent; // Clear sort for this column
      }

      // Add new rule. If you want this column to be highest priority, unshift instead of push.
      return [...withoutCurrent, { column: currentCol, direction }];
    });

    handleMenuClose();
    // setCurrentPageNumber(1); // reset to page 1 after sort change
  };


  //if (rows.length === 0) return <div>No data to display</div>;

  return (
    <div className="rd-data-table-container">
      <div className="rd-data-table-scroll">
        <table className="rd-data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.value} style={{ position: "relative" }}>
                  {col.label}

                  <IconButton
                    size="small"
                    onClick={e => handleMenuOpen(e, getColumnDataKey(col))}
                    style={{ marginLeft: 4 }}
                  >
                    <Iconography
                      icon="arrowUpArrowDown"
                      size={IconographySizes.Small}
                    />
                  </IconButton>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {totalRecords === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  No data to display
                </td>
              </tr>
            ) : (
              pagedRows
                .filter((row) => {
                  // a row is empty if every column resolves to an empty value
                  return !columns.every((col) => {
                    const key = getColumnDataKey(col);
                    const v = getValue(row, key);
                    return (
                      v == null || // undefined or null
                      (typeof v === "string" && v.trim() === "") // empty/whitespace string
                    );
                  });
                })
                .map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((col, cIdx) => {
                      const key = getColumnDataKey(col);
                      const cell = getValue(row, key);
                      return <td key={`${col.value}-${cIdx}`}>
                        {col.label === "Registration Date" && cell
                          ? moment(cell).format("DD/MM/YYYY")
                          : (cell ?? "")}
                        </td>
                    })}
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
      <div className="rd-data-table-footer">
        <Pagination
          currentPageNumber={currentPageNumber}
          totalRecords={totalRecords}
          pageSize={pageSize}
          onPageNumberClick={handlePageChange}
          onPageSizesClick={onPageSizesClick}
          pageSizes={[
            { label: "15", value: "15" },
            { label: "20", value: "20" },
            { label: "50", value: "50" },
            { label: "100", value: "100" },
          ]}
        />
      </div>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleSortChange("asc")}>
          Ascending
        </MenuItem>
        <MenuItem onClick={() => handleSortChange("desc")}>
          Descending
        </MenuItem>
        <MenuItem onClick={() => handleSortChange("")}>
          Clear
        </MenuItem>
      </Menu>
    </div>
  );
};

export default DataTable;